import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import type { ICreateConfigurationInfo, IConfigurationInfo } from "../../interfaces";
import { publishApiError } from "../../events";
import { settingsRequest } from "../../authConfig";
import { sendDELETERequest, sendGETRequest, sendPOSTRequest, sendPUTRequest } from "../requestHelper/requestHelper";

const settingsBaseUrl = import.meta.env.VITE_APP_BASE_SETTINGS_URL;

const settingsConfig = {
    allSettingsEndpoint: `${settingsBaseUrl}/api/apps/settings`
};

const getCreateConfigurationEndpoint = (appId: string): string => {
    return `${settingsBaseUrl}/api/apps/${appId}/settings`;
};

const getSpecificConfigurationEndpoint = (appId: string, itemId: string): string => {
    return `${settingsBaseUrl}/api/apps/${appId}/settings/id/${itemId}`;
};

//Get all items for a tenant: [GET] /api/apps/settings
export async function fetchConfigurationsForTenantAsync(instance: IPublicClientApplication, account: AccountInfo): Promise<IConfigurationInfo[] | null> {
    try {
        const authResult: AuthenticationResult = await instance.acquireTokenSilent({ ...settingsRequest, account });
        const result = await sendGETRequest<IConfigurationInfo[]>(settingsConfig.allSettingsEndpoint, authResult.accessToken);
        return result;
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return null;
    }
}

// Create a new item: [POST] /api/apps/{appId}/settings
export async function createConfigurationAsync(instance: IPublicClientApplication, account: AccountInfo, settingsInfo: ICreateConfigurationInfo): Promise<void> {
    try {
        const authResult: AuthenticationResult = await instance.acquireTokenSilent({ ...settingsRequest, account });
        const result = await sendPOSTRequest<undefined, ICreateConfigurationInfo>(getCreateConfigurationEndpoint(settingsInfo.appId), authResult.accessToken, settingsInfo);
        return result;
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return;
    }
}


// Get, Delete, or Update a specific item: [GET, DELETE, PUT] /api/apps/{appId}/settings/{itemId}
export async function fetchConfigurationAsync(instance: IPublicClientApplication, account: AccountInfo, appId: string, itemId: string): Promise<IConfigurationInfo | null> {
    try {
        const authResult: AuthenticationResult = await instance.acquireTokenSilent({ ...settingsRequest, account });
        const result = await sendGETRequest<IConfigurationInfo>(getSpecificConfigurationEndpoint(appId, itemId), authResult.accessToken);
        return result;
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return null;
    }
}

export async function updateConfigurationAsync(instance: IPublicClientApplication, account: AccountInfo, settingsInfo: IConfigurationInfo): Promise<void> {
    try {
        const authResult: AuthenticationResult = await instance.acquireTokenSilent({ ...settingsRequest, account });
        const result = await sendPUTRequest<undefined, IConfigurationInfo>(getSpecificConfigurationEndpoint(settingsInfo.appId, settingsInfo.id), authResult.accessToken, settingsInfo);
        return result;
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return;
    }
}

export async function deleteConfigurationAsync(instance: IPublicClientApplication, account: AccountInfo, appId: string, itemId: string): Promise<void> {
    try {
        const authResult: AuthenticationResult = await instance.acquireTokenSilent({ ...settingsRequest, account });
        const result = await sendDELETERequest<undefined>(getSpecificConfigurationEndpoint(appId, itemId), authResult.accessToken);
        return result;
    } catch (error) {
        console.debug(error);
        publishApiError((error as Error)?.message);
        return;
    }
}